/**
 =========================================================
 * Material Dashboard 2 React - v2.2.0
 =========================================================
 
 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
 All of the routes for the Material Dashboard 2 React are added here,
 You can add a new route, customize the routes and delete the routes here.
 
 Once you add a new route on this file it will be visible automatically on
 the Sidenav.
 
 For adding a new route you can follow the existing routes in the routes array.
 1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
  */
import React from "react";
import { Navigate } from "react-router-dom";

// Material Dashboard 2 React layouts
import Cover from "~/layouts/authentication/reset-password/cover";
import SignIn from "~/layouts/authentication/sign-in";

// @mui icons
import Icon from "@mui/material/Icon";
import Dashboard from "~/layouts/dashboard";
import PlantMonitoringTable from "~/pages/PlantSurvey/PlantMonitoringTable";
import ProtectedRoute from "~/routes/ProtectedRoute";

import PlantDetails from "~/pages/public/PlantDetails";

import ChangePassword from "~/layouts/authentication/reset-password/cover/ChangePassword";

import PlantSurveyList from "~/pages/Dashboard/PlantSurveyList";

import Plantations from "~/pages/Dashboard/Plantations";

import Plants from "~/pages/Dashboard/Plants";
import Plots from "~/pages/Dashboard/Plots";
import PlantSurvey from "~/pages/PlantSurvey";

import ReportData from "~/pages/ReportData";

import PlantationDetails from "~/pages/PlantationDetails";

import CreatePlantation from "~/pages/PlantationDetails/CreatePlantation";
import QrCodeGeneration from "~/pages/PlantationDetails/QrCodeGeneration";
import CategoryMaster from "~/pages/admin/master/Category";
import CreateCategory from "~/pages/admin/master/Category/CreateCategory";
import PlantMaster from "~/pages/admin/master/Plant";
import CreatePlant from "~/pages/admin/master/Plant/CreatePlant";
import LocationMaster from "~/pages/admin/master/location";
import CreateLocation from "~/pages/admin/master/location/CreateLocation";
import PlottingMaster from "~/pages/admin/master/plotting";

import PageLayout from "~/examples/LayoutContainers/PageLayout";
import CreatePlotting from "~/pages/admin/master/plotting/CreatePlotting";
import Eula from "~/pages/public/Eula";
import PrivacyPolicy from "~/pages/public/PrivacyPolicy";
import TermsConditions from "~/pages/public/TermsConditions";
import { roles } from "./config";
import GoogleMapsNew from "./components/GoogleMapsNew";

const createDynamicRoute = (routeType, routePath, icon, children) => ({
  type: "route",
  name: routeType,
  key: routeType,
  icon: <Icon fontSize="small">{icon}</Icon>,
  route: routePath,
  component: React.cloneElement(children),
});

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <Dashboard />,
      </ProtectedRoute>
    ),
  },
  {
    type: "item",
    name: "Plots",
    key: "plots",
    icon: <Icon fontSize="small">place</Icon>,
    route: "/dashboard/location/:plotID",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <Plots />,
      </ProtectedRoute>
    ),
  },
  {
    type: "route",
    name: "Plantation",
    key: "plantation",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard/location/plots/:plantID",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <Plantations />,
      </ProtectedRoute>
    ),
  },
  {
    type: "route",
    name: "Maps",
    key: "maps",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/maps",
    component: (
      <GoogleMapsNew />
    )
  },
  {
    type: "route",
    name: "Create Plotting Master",
    key: "Create Plotting Master",
    icon: <Icon fontSize="small">place</Icon>,
    route: "/dashboard/location/plots/plantation/:plantID",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <Plants />,
      </ProtectedRoute>
    ),
  },
  createDynamicRoute(
    "Plant Survey List",
    "/dashboard/location/plots/plantation/plant-survey/:id",
    "category",
    <PlantSurveyList />
  ),
  createDynamicRoute(
    "QR Code Page",
    "/plantation-master/qr-code/:id",
    "category",
    <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
      <QrCodeGeneration />
    </ProtectedRoute>
  ),
  {
    type: "collapse",
    name: "Category Master",
    key: "category-master",
    icon: <Icon fontSize="small">category</Icon>,
    route: "/category-master",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <CategoryMaster />,
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Plant Master",
    key: "plant-master",
    icon: <Icon fontSize="small">grass_outlined</Icon>,
    route: "/plant-master",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <PlantMaster />,
      </ProtectedRoute>
    ),
  },

  {
    type: "collapse",
    name: "Location Master",
    key: "location-master",
    icon: <Icon fontSize="small">place</Icon>,
    route: "/location-master",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <LocationMaster />,
      </ProtectedRoute>
    ),
  },

  ...["create", "update"].map((item) =>
    createDynamicRoute(
      item,
      `/location-master/${item}/:locationId?`,
      "place",
      <CreateLocation />
    )
  ),

  {
    type: "collapse",
    name: "Plotting Master",
    key: "plotting-master",
    icon: <Icon fontSize="small">attractions</Icon>,
    route: "/plotting-master",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <PlottingMaster />,
      </ProtectedRoute>
    ),
  },
  ...["create", "update"].map((item) =>
    createDynamicRoute(
      item,
      `/plotting-master/${item}/:plottingId?`,
      "category",
      <CreatePlotting />
    )
  ),
  {
    type: "collapse",
    name: "Plantation Master",
    key: "plantation-master",
    icon: <Icon fontSize="small">forest</Icon>,
    route: "/plantation-master",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <PlantationDetails />,
      </ProtectedRoute>
    ),
  },

  ...["create", "update"].map((item) =>
    createDynamicRoute(
      item,
      `/plant-master/${item}/:plantId?`,
      "yard_outlined",
      <CreatePlant />
    )
  ),
  /* new changes - tag v3.0.1 */
  ...["create", "update"].map((item) =>
    createDynamicRoute(
      item,
      `/plantation-master/${item}/:plantationId?`,
      "yard_outlined",
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <CreatePlantation />,
      </ProtectedRoute>
    )
  ),
  // {
  //   type: "route",
  //   name: "Create Plantation Details",
  //   key: "create",
  //   icon: <Icon fontSize="small">yard_outlined</Icon>,
  //   route: "/plantation-master/create/:plantationId?",
  //   component: (
  //     <ProtectedRoute>
  //       <CreatePlantation />,
  //     </ProtectedRoute>
  //   ),
  // },

  ...["create", "update"].map((item) =>
    createDynamicRoute(
      item,
      `/${item}/category-master/:catId?`,
      "category",
      <CreateCategory />
    )
  ),

  {
    type: "item",
    name: "Plant Monitoring",
    key: "plant-monitoring",
    icon: <Icon fontSize="small">park</Icon>,
    route: "/plant-monitoring/:id?",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <PlantSurvey />,
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Plant Monitoring",
    key: "plant-monitoring",
    icon: <Icon fontSize="small">park</Icon>,
    route: "/plant-monitoring",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <PlantSurvey />,
      </ProtectedRoute>
    ),
  },
  {
    type: "collapse",
    name: "Reports",
    key: "reports",
    icon: <Icon fontSize="small">assessment</Icon>,
    route: "/reports",
    component: (
      <ProtectedRoute roles={[roles.ADMIN]}>
        <ReportData />,
      </ProtectedRoute>
    ),
  },
  // FIXME: FIX this issue as when routing to PlantMonitoring list it's navigating to the sign-in page
  {
    type: "route",
    name: "Monitoring List",
    key: "monitoring-list",
    icon: <Icon fontSize="small">yard_outlined</Icon>,
    route: "/plant-monitoring/list",
    component: (
      <ProtectedRoute roles={[roles.ADMIN, roles.USER]}>
        <PlantMonitoringTable />,
      </ProtectedRoute>
    ),
  },
  // {
  //   type: 'collapse',
  //   name: 'Tables',
  //   key: 'tables',
  //   icon: <Icon fontSize="small">table_view</Icon>,
  //   route: '/tables',
  //   component: <Tables />,
  // },
  // {
  //   type: 'collapse',
  //   name: 'Billing',
  //   key: 'billing',
  //   icon: <Icon fontSize="small">receipt_long</Icon>,
  //   route: '/billing',
  //   component: <Billing />,
  // },
  // {
  //   type: 'collapse',
  //   name: 'RTL',
  //   key: 'rtl',
  //   icon: <Icon fontSize="small">format_textdirection_r_to_l</Icon>,
  //   route: '/rtl',
  //   component: <RTL />,
  // },
  // {
  //   type: "collapse",
  //   name: "Notifications",
  //   key: "notifications",
  //   icon: <Icon fontSize="small">notifications</Icon>,
  //   route: "/notifications",
  //   component: <Notifications />,
  // },
  // {
  //   type: 'collapse',
  //   name: 'Profile',
  //   key: 'profile',
  //   icon: <Icon fontSize="small">person</Icon>,
  //   route: '/profile',
  //   component: <Profile />,
  // },
  {
    type: "route",
    name: "terms",
    key: "terms",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/terms-condition",
    component: (
      <PageLayout>
        <TermsConditions />
      </PageLayout>
    ),
  },
  {
    type: "route",
    name: "eula",
    key: "eula",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/eula",

    component: (
      <PageLayout>
        <Eula />
      </PageLayout>
    ),
  },
  {
    type: "route",
    name: "policy",
    key: "policy",
    icon: <Icon Size="small">login</Icon>,
    route: "/privacy-policy",
    component: (
      <PageLayout>
        {" "}
        <PrivacyPolicy />
      </PageLayout>
    ),
  },
  {
    type: "route",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/plant/:qrCode?",
    component: <PlantDetails />,
  },
  {
    type: "route",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
  },
  {
    type: "route",
    name: "Forget Password",
    key: "forget-password",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/forget-password",
    component: <Cover />,
  },
  {
    type: "item",
    name: "Change Password",
    key: "change-password",
    icon: <Icon fontSize="small">lockreset</Icon>,
    route: "/change-password",
    component: <ChangePassword />,
  },
  {
    type: "item",
    name: "wildcard_route",
    key: "wildcard_route",
    icon: <Icon fontSize="small">lockreset</Icon>,
    route: "*",
    component: <Navigate to={"/dashboard"} />,
  },
  // {
  //   type: 'collapse',
  //   name: 'Sign Up',
  //   key: 'sign-up',
  //   icon: <Icon fontSize="small">assignment</Icon>,
  //   route: '/authentication/sign-up',
  //   component: <SignUp />,
  // },
];

export default routes;
